import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '../services/api'
import type { User } from '../services/authService'
import { sseService } from '../services/sseService'
import { useNotificationStore } from './notifications'
import { usePreferencesStore } from './preferences'
import { errorLog } from '@/utils/debug'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(localStorage.getItem('access_token'))
  const refreshToken = ref<string | null>(localStorage.getItem('refresh_token'))
  const tokenExpiresAt = ref<number | null>(
    localStorage.getItem('token_expires_at')
      ? parseInt(localStorage.getItem('token_expires_at')!)
      : null,
  )
  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false) // Track if auth state has been initialized
  let refreshTimer: number | null = null
  let initializationCallbacks: (() => void)[] = [] // Callbacks waiting for initialization

  // Computed
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const subscriptionTier = computed(() => user.value?.subscription?.tier || 'free')
  const usageStats = computed(() => user.value?.usage)
  const isLifetimeMember = computed(() => subscriptionTier.value === 'lifetime')
  const isYearlyMember = computed(() => subscriptionTier.value === 'yearly')
  const isAdmin = computed(() => user.value?.role === 'admin')
  const hasUnlimitedCredits = computed(() => isAdmin.value)
  const canGenerateContent = computed(() => {
    if (!user.value) return false
    if (isAdmin.value) return true
    return user.value.usage.remaining_credits > 0
  })
  const canGenerateVideo = computed(() => {
    if (!user.value) return false
    if (isAdmin.value) return true
    // Video generation costs 5 credits
    return user.value.usage.remaining_credits >= 5
  })

  // Check if user has an active paid subscription
  const hasActiveSubscription = computed(() => {
    if (!user.value) return false
    if (isAdmin.value) return true // Admins always have access

    const tier = user.value.subscription?.tier
    const status = user.value.subscription?.status

    // Free tier = no subscription
    if (!tier || tier === 'free') return false

    // Paid tiers (monthly, yearly, lifetime) grant access
    const paidTiers = ['monthly', 'yearly', 'lifetime']
    if (paidTiers.includes(tier)) {
      // For canceled subscriptions, check if still within billing period
      if (status === 'canceled') {
        const periodEnd = user.value.subscription?.current_period_end
        if (!periodEnd || new Date(periodEnd) <= new Date()) {
          return false // Canceled and period ended
        }
      }
      return true // Has paid tier and not expired
    }

    return false
  })

  // Helper to store session
  function storeSession(session: { access_token: string; refresh_token: string; expires_at?: number; expires_in?: number }) {

    accessToken.value = session.access_token
    refreshToken.value = session.refresh_token

    localStorage.setItem('access_token', session.access_token)
    localStorage.setItem('refresh_token', session.refresh_token)

    // Store expiration time
    // Supabase returns expires_at as Unix timestamp in seconds, or expires_in as seconds
    let expiresAt: number
    if (session.expires_at) {
      // expires_at is in seconds, convert to milliseconds
      expiresAt = session.expires_at * 1000
    } else if (session.expires_in) {
      // expires_in is seconds from now
      expiresAt = Date.now() + session.expires_in * 1000
    } else {
      // Default to 1 hour
      expiresAt = Date.now() + 3600 * 1000
    }

    tokenExpiresAt.value = expiresAt
    localStorage.setItem('token_expires_at', expiresAt.toString())

    // Schedule token refresh
    scheduleTokenRefresh()
  }

  // Helper to clear session
  function clearSession() {
    accessToken.value = null
    refreshToken.value = null
    tokenExpiresAt.value = null
    user.value = null

    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('token_expires_at')

    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
  }

  // Schedule automatic token refresh
  function scheduleTokenRefresh() {
    if (refreshTimer) {
      clearTimeout(refreshTimer)
    }

    if (!tokenExpiresAt.value || !refreshToken.value) {
      return
    }

    // Refresh token 5 minutes before expiration
    const timeUntilRefresh = tokenExpiresAt.value - Date.now() - 5 * 60 * 1000

    if (timeUntilRefresh > 0) {
      refreshTimer = window.setTimeout(() => {
        refreshAccessToken()
      }, timeUntilRefresh)
    } else {
      // Token is already expired or about to expire, refresh now
      refreshAccessToken()
    }
  }

  // Refresh access token
  async function refreshAccessToken() {
    if (!refreshToken.value) {

      await logout()
      return false
    }

    try {

      const response = await api.refreshToken(refreshToken.value)

      if (!response.success || !response.session) {

        // Only logout if it's truly invalid, not on network errors
        if (response.error?.includes('Invalid') || response.error?.includes('expired')) {
          await logout()
        }
        return false
      }

      storeSession(response.session)
      return true
    } catch {
      // Don't logout on network errors, let them retry
      return false
    }
  }

  // Actions
  async function signup(email: string, password: string) {
    loading.value = true
    error.value = null

    try {
      const response = await api.signup(email, password)

      if (!response.success) {
        error.value = response.error || 'Signup failed'
        return { success: false, error: error.value }
      }

      // Check if email confirmation is required
      if (response.email_confirmation_required || !response.session) {
        return {
          success: true,
          emailConfirmationRequired: true,
          message: 'Please check your email to confirm your account, then login.',
        }
      }

      // Auto-login if session is provided
      if (response.session) {
        storeSession(response.session)
        await loadProfile()
      }

      return { success: true }
    } catch (err: any) {
      error.value = err.message || 'Network error'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function login(email: string, password: string) {
    loading.value = true
    error.value = null

    try {
      const response = await api.login(email, password)

      if (!response.success || !response.session) {
        error.value = response.error || 'Login failed'
        return { success: false, error: error.value }
      }

      storeSession(response.session)
      await loadProfile()

      // Connect to SSE for real-time notifications
      sseService.connect(response.session.access_token)

      return { success: true }
    } catch (err: any) {
      error.value = err.message || 'Network error'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    loading.value = true
    error.value = null

    // Disconnect SSE before logout
    sseService.disconnect()

    // Clear user-specific data to prevent data leakage between accounts
    const notificationStore = useNotificationStore()
    notificationStore.clearAll()

    const preferencesStore = usePreferencesStore()
    preferencesStore.resetPreferences()

    try {
      await api.logout()
    } catch {
      // Continue logout even if API call fails
    } finally {
      clearSession()
      loading.value = false
    }
  }

  async function signInWithApple() {
    loading.value = true
    error.value = null

    try {
      const response = await api.signInWithApple()

      if (!response.success || !response.data?.url) {
        error.value = response.error || 'Failed to initiate Apple Sign In'
        return { success: false, error: error.value }
      }

      // Redirect to Apple OAuth
      window.location.href = response.data.url
      return { success: true }
    } catch (err: any) {
      error.value = err.message || 'Network error'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function signInWithGoogle() {
    loading.value = true
    error.value = null

    try {
      const response = await api.signInWithGoogle()

      if (!response.success || !response.data?.url) {
        error.value = response.error || 'Failed to initiate Google Sign In'
        return { success: false, error: error.value }
      }

      // Redirect to Google OAuth
      window.location.href = response.data.url
      return { success: true }
    } catch (err: any) {
      error.value = err.message || 'Network error'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function signInWithFacebook() {
    loading.value = true
    error.value = null

    try {
      const response = await api.signInWithFacebook()

      if (!response.success || !response.data?.url) {
        error.value = response.error || 'Failed to initiate Facebook Sign In'
        return { success: false, error: error.value }
      }

      // Redirect to Facebook OAuth
      window.location.href = response.data.url
      return { success: true }
    } catch (err: any) {
      error.value = err.message || 'Network error'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function devLogin(email: string, code?: string) {
    loading.value = true
    error.value = null

    try {
      const response = await api.devLogin(email, code)

      if (!response.success || !response.session) {
        error.value = response.error || 'Dev login failed'
        return { success: false, error: error.value }
      }

      storeSession(response.session)
      await loadProfile()

      // Connect to SSE for real-time notifications
      sseService.connect(response.session.access_token)

      return { success: true }
    } catch (err: any) {
      error.value = err.message || 'Network error'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function loadProfile() {
    // Check localStorage for token if not in state
    if (!accessToken.value) {
      const storedToken = localStorage.getItem('access_token')
      if (storedToken) {
        accessToken.value = storedToken
      } else {
        markInitialized()
        return
      }
    }

    loading.value = true
    error.value = null

    try {

      const response = await api.getProfile()

      // Handle both response structures: response.data.user OR response.user
      const userData = response.data?.user || (response as any).user

      // Check if account was deleted - clear everything and let user login fresh
      if ((response as any).code === 'ACCOUNT_DELETED') {
        clearSession()
        markInitialized()
        return
      }

      if (!response.success || !userData) {

        // Token is invalid, try to refresh
        const refreshed = await refreshAccessToken()
        if (refreshed) {

          // Try loading profile again with new token (but only once to avoid infinite loop)
          loading.value = true
          const retryResponse = await api.getProfile()

          // Check for account deleted on retry too
          if ((retryResponse as any).code === 'ACCOUNT_DELETED') {
            clearSession()
            markInitialized()
            return
          }

          // Handle both response structures
          const retryUserData = retryResponse.data?.user || (retryResponse as any).user

          if (retryResponse.success && retryUserData) {
            user.value = retryUserData

            // Sync subscription after successful login
            syncSubscriptionInBackground()
          } else {

            await logout()
          }
          return
        } else {
          // Refresh failed, clear auth state

          await logout()
          return
        }
      }

      user.value = userData

      // Sync subscription after successful profile load
      syncSubscriptionInBackground()
    } catch (err: any) {

      error.value = err.message || 'Failed to load profile'
      // Try to refresh token on error (but don't logout immediately on network errors)
      const refreshed = await refreshAccessToken()
      if (refreshed) {
        // Retry profile load after successful refresh
        try {
          const retryResponse = await api.getProfile()
          if ((retryResponse as any).code === 'ACCOUNT_DELETED') {
            clearSession()
            return
          }
          if (retryResponse.success && retryResponse.data?.user) {
            user.value = retryResponse.data.user
            syncSubscriptionInBackground()
          }
        } catch {
          // Silent fail on retry
        }
      }
    } finally {
      loading.value = false
      markInitialized()
    }
  }

  // Sync subscription in background without blocking
  async function syncSubscriptionInBackground() {
    try {

      const response = await api.syncSubscription()

      if (response.success) {

        // Refresh profile to get updated data
        const profileResponse = await api.getProfile()
        if (profileResponse.success && profileResponse.data?.user) {
          user.value = profileResponse.data.user
        }
      }
    } catch {
      // Silent fail - don't disrupt user experience
    }
  }

  async function refreshProfile() {
    await loadProfile()
  }

  // Check if token is expired or about to expire
  function isTokenExpired(): boolean {
    if (!tokenExpiresAt.value) return false
    // Consider expired if less than 1 minute remaining
    return Date.now() >= tokenExpiresAt.value - 60 * 1000
  }

  // Mark initialization as complete and notify all waiting callbacks
  function markInitialized() {
    if (initialized.value) return
    initialized.value = true
    // Notify all waiting callbacks
    initializationCallbacks.forEach((callback) => callback())
    initializationCallbacks = []
  }

  // Wait for initialization to complete
  async function waitForInitialization(): Promise<void> {
    if (initialized.value) return

    return new Promise((resolve) => {
      initializationCallbacks.push(resolve)

      // Timeout after 5 seconds as fallback
      setTimeout(() => {
        if (!initialized.value) {
          markInitialized()
        }
      }, 5000)
    })
  }

  // Initialize auth state on store creation
  if (accessToken.value && refreshToken.value) {
    // Only refresh if token is truly expired (less than 1 minute left)
    if (isTokenExpired()) {
      refreshAccessToken().then((success) => {
        if (success) {
          loadProfile().then(() => {
            // Connect SSE after successful profile load
            if (accessToken.value) {
              sseService.connect(accessToken.value)
            }
          }).catch((error) => {
            errorLog('Failed to load profile after token refresh:', error)
            markInitialized()
          })
        } else {
          markInitialized()
        }
      }).catch((error) => {
        errorLog('Failed to refresh access token:', error)
        markInitialized()
      })
    } else {
      // Token is still valid, just load profile
      loadProfile().then(() => {
        // Connect SSE after successful profile load
        if (accessToken.value) {
          sseService.connect(accessToken.value)
        }
      }).catch((error) => {
        errorLog('Failed to load profile:', error)
        markInitialized()
      })
      scheduleTokenRefresh()
    }
  } else {
    // No tokens, mark as initialized
    markInitialized()
  }

  async function storeSessionAndLoad(session: { access_token: string; refresh_token: string; expires_at?: number; expires_in?: number }) {
    storeSession(session)
    await loadProfile()
    sseService.connect(session.access_token)
  }

  return {
    // State
    user,
    accessToken,
    refreshToken,
    loading,
    error,
    initialized,

    // Computed
    isAuthenticated,
    subscriptionTier,
    usageStats,
    isLifetimeMember,
    isYearlyMember,
    isAdmin,
    hasUnlimitedCredits,
    canGenerateContent,
    canGenerateVideo,
    hasActiveSubscription,

    // Actions
    signup,
    login,
    logout,
    devLogin,
    signInWithApple,
    signInWithGoogle,
    signInWithFacebook,
    loadProfile,
    storeSessionAndLoad,
    refreshProfile,
    refreshAccessToken,
    waitForInitialization,
  }
})
